import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Croissant, Heart, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { BakeryCrawlCard } from "@/components/BakeryCrawl";
import { BAKERIES } from "@/data/copenhagen";
import { useActiveAds, adsForCategory } from "@/lib/ads";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";

const mapUrl = (name, a) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${name}, ${a.street}, ${a.locality}, Denmark`
  )}`;

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/qucgmzdz_20250213_122646.webp";

// Our hand-picked favourites — get the warm highlight badge on the card.
const FAVOURITES = new Set([
  "Sankt Peders Bageri",
  "Bageriet BRØD",
  "Bosses Bager",
  "Det Rene Brød",
  "Eriks Bageri",
]);

// The wienerbrød family — what the world calls "a Danish". Glosses kept to a
// line so the card scans like a field guide.
const WIENERBROD = [
  { slug: "kanelsnegle", name: "Kanelsnegle", note: "The cinnamon swirl — the everyday classic." },
  { slug: "kardemommesnurre", name: "Kardemommesnurre", note: "The cardamom bun — the one worth queuing for." },
  { slug: "spandauer", name: "Spandauer", note: "The custard one — the original 'Danish pastry'." },
  { slug: "tebirkes", name: "Tebirkes", note: "Flaky layers, remonce filling, poppy seeds on top." },
  { slug: "rosenbroed", name: "Rosenbrød", note: "The 'rose bread' — mostly white-iced, pure nostalgia." },
  { slug: "romsnegl", name: "Romsnegl", note: "The rum snail — a big, flat spiral with a rum glaze." },
  { slug: "overskaaren", name: "Overskåren", note: "An old classic — flaky, cream in the middle, glaze on each side." },
  { slug: "brunsviger", name: "Brunsviger", note: "Soft brown-sugar cake — a Funen favourite." },
  { slug: "chokoladebolle", name: "Chokoladebolle", note: "Big flaky chocolate bun — a kids' favourite." },
  { slug: "droemmekage", name: "Drømmekage", note: "The 'dream cake' — soft sponge under a coconut-caramel lid." },
];

// The bakery crawl route data lives in data/bakeryCrawl.js — shared with the
// dedicated /bakery-crawl page (which carries the route's ItemList JSON-LD)
// and mirrored in the planner as the BAKERY_WALK one-tap day, Feb–Mar.
const SITE = "https://cphhighlights.com";

// The big bakery FAQ — answers stay plain strings so the FAQPage schema
// reuses them; links render under the answer (link / links fields).
const FAQS = [
  {
    q: "What is the best bakery in Copenhagen?",
    a: "Juno the Bakery in Østerbro carries the international hype — the cardamom bun is genuinely worth the queue. But the bench is deep: Hart Bageri for sourdough and morning buns, Lille Bakery on Refshaleøen for the BMO, Sankt Peders Bageri for the Wednesday cinnamon swirl, and La Glace for old-school layer cakes. Try a few — that's what the bakery crawl is for.",
    link: { to: "/bakery-crawl", label: "The bakery crawl" },
  },
  {
    q: "What do Danes call a Danish pastry?",
    a: "Wienerbrød — 'Viennese bread' — a nod to the Austrian bakers who brought the technique to Denmark in the 1800s. It's a whole family of pastries, and every one has its own name: kanelsnegl, spandauer, tebirkes and the rest. Order 'a Danish' and you'll get a smile; order a kardemommesnurre and you'll get respect.",
  },
  {
    q: "What should I order at a Danish bakery?",
    a: "Start with a kanelsnegl (cinnamon swirl) or a kardemommesnurre (cardamom bun) — the two benchmarks every bakery is judged by. Then a spandauer, the custard original, and a tebirkes with its poppy seeds and soft marzipan centre. The field guide above has the full line-up.",
  },
  {
    q: "Which cakes should I try in Copenhagen?",
    a: "Drømmekage — the 'dream cake', soft sponge under a coconut-caramel lid — brunsviger, the sticky brown-sugar coffee cake from Funen, and kajkage, the green marzipan-topped childhood favourite named after a TV puppet. For the grand old layer cakes, La Glace has been at it since 1870.",
  },
  {
    q: "Which bakery in Copenhagen is the most old school?",
    a: "Bosses Bager on Østerbro and Eriks Bageri on Amagerbrogade — two proper old-school bakeries where the counter is all classics: real Danish pastries and cakes in generous, old-fashioned sizes, made the way they've always been. No cardamom-bun hype, no queue — just a good old Danish bakery.",
    link: { to: "/areas/amager", label: "The Amager guide" },
  },
  {
    q: "Is Juno the Bakery worth the queue?",
    a: "It depends — the cardamom bun is good, but if the line is an hour it's not worth it. Go to nearby Bosse instead and buy some old-school bakery classics.",
  },
  {
    q: "What time do bakeries open in Copenhagen?",
    a: "Early — most open at 6 or 7 in the morning, and the popular ones start selling out of the favourites by late morning. Go before 11 for the full counter; afternoon visits are for the flexible.",
  },
  {
    q: "Are bakeries open on Sundays in Copenhagen?",
    a: "Yes — Sunday morning at the bakery is a Danish ritual, and nearly every spot on this page opens seven days a week. One notable exception: Sankt Peders Bageri is closed on Sundays. Expect the biggest queues between 9 and 11 on weekend mornings.",
  },
  {
    q: "What is a kanelsnegl?",
    a: "The cinnamon swirl — Denmark's everyday classic and the pastry most visitors fall for first: flaky, buttery dough rolled with cinnamon sugar. Sankt Peders Bageri's giant Wednesday version (onsdagssnegl) is the city's most famous, but any bakery on this page does a good one.",
  },
  {
    q: "What is a fastelavnsbolle?",
    a: "Denmark's Shrovetide bun — a cream-filled calorie bomb every bakery makes its own version of, from early January until Fastelavn in February or early March. After that they vanish for the year. Visiting in season? Trying a few different ones is practically a sport.",
  },
  {
    q: "How much does a pastry cost in Copenhagen?",
    a: "Expect roughly 30–50 kr. for a wienerbrød at the good bakeries — the famous cardamom buns and filled croissants sit at the top end. Coffee and a pastry lands around 80–100 kr. Not cheap, but this is one of the things Copenhagen genuinely does best in the world.",
  },
  {
    q: "Can I do a bakery tour of Copenhagen?",
    a: "Yes — the bakery crawl on this page strings the best stops into one walking route with a Google Maps link. Do it over a morning, share everything, and judge each place on its cardamom bun.",
    link: { to: "/bakery-crawl", label: "Open the bakery crawl" },
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};
const BAKERY_LIST = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "The best bakeries in Copenhagen",
  description:
    "Local-picked Copenhagen bakeries — with the wienerbrød field guide, the seasonal fastelavnsbolle explainer, and a ready-made walking route linking them all.",
  itemListElement: BAKERIES.map((b, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Bakery",
      name: b.name,
      address: b.addresses.map((a) => ({
        "@type": "PostalAddress",
        streetAddress: a.street,
        addressLocality: a.locality,
        addressCountry: "DK",
      })),
    },
  })),
};

export default function BakeriesPage() {
  const bakeryAds = adsForCategory(useActiveAds(), "Bakery & café");
  return (
    <main data-testid="bakeries-page" className="pt-16 md:pt-20">
      <Seo
        title="Best Bakeries in Copenhagen: 10+ Places Worth the Queue"
        description="The must-try bakeries in Copenhagen — cardamom buns worth queuing for, a wienerbrød field guide, and a ready-made walking route that links them all."
        path="/bakeries"
        image="https://images.pexels.com/photos/38354165/pexels-photo-38354165.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BAKERY_LIST)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <PageSchema
        name="Best Bakeries in Copenhagen: 10+ Places Worth the Queue"
        description="The must-try bakeries in Copenhagen — cardamom buns and Danish pastries worth queuing for, with what to order and where to find them."
        path="/bakeries"
        crumb="Bakeries"
      />
      {/* page header */}
      <section className="relative bg-[#13141A] text-white overflow-hidden border-b border-white/20">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="A seeded Danish croissant and a cup of coffee at a Copenhagen café"
            className="w-full h-full object-cover object-[center_35%] opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="bakeries-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="bakeries-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">A NATIONAL OBSESSION</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            The Best Bakeries<br />
            <span className="font-editorial italic font-600">in Copenhagen</span>
          </motion.h1>
          <p className="font-editorial italic font-600 text-xl md:text-2xl text-white mt-4">
            Every bakery worth the queue.
          </p>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6">
            Danes take their bakery goods seriously. Cardamom buns, danish pastries and sourdough
            are a genuine reason to visit.
          </p>
        </div>
      </section>

      {/* bakery grid */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 pb-8 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {bakeryAds.map((ad) => (
              <div
                key={ad.id}
                data-testid={`bakery-partner-${ad.id}`}
                className="group bg-white text-black border border-zinc-200 shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all p-4 md:p-7 h-full flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                    <MapPin size={12} /> {ad.area.replace(/-/g, " ").toUpperCase()}
                  </span>
                  <span className="font-body font-800 text-[10px] tracking-widest text-[#F59E0B]">SPONSORED</span>
                </div>
                {ad.website ? (
                  <a
                    href={ad.website}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    data-testid={`bakery-partner-link-${ad.id}`}
                    className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-4 leading-none inline-block underline decoration-1 underline-offset-2 decoration-black/25 hover:decoration-[#DF3226] hover:text-[#DF3226] transition-colors"
                  >
                    {ad.business_name}
                  </a>
                ) : (
                  <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-4 leading-none">
                    {ad.business_name}
                  </h3>
                )}
                <p className="font-body text-sm md:text-base text-black/70 mt-4 leading-relaxed">{ad.description}</p>
              </div>
            ))}
            {BAKERIES.map((b, i) => (
              <Reveal key={b.name} delay={(i % 3) * 0.05} y={40}>
                <div
                  data-testid={`bakery-card-${i}`}
                  className="group bg-white text-black border border-zinc-200 shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all p-4 md:p-7 h-full flex flex-col"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                      <MapPin size={12} /> {b.area.toUpperCase()}
                    </span>
                    {FAVOURITES.has(b.name) && (
                      <span
                        data-testid={`bakery-favourite-${i}`}
                        className="inline-flex items-center gap-1 font-body font-800 text-[10px] tracking-widest text-[#B8860B] whitespace-nowrap"
                      >
                        <Heart size={11} className="fill-[#B8860B]" /> OUR FAVOURITE
                      </span>
                    )}
                  </div>
                  <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-2.5 md:mt-4 leading-none">
                    {b.name}
                  </h3>
                  <div className="inline-flex self-start items-center gap-1.5 mt-2.5 md:mt-3 bg-[#DF3226] text-white border border-zinc-200 px-2.5 py-1">
                    <Croissant size={13} />
                    <span className="font-body font-800 text-xs">{b.signature}</span>
                  </div>
                  <p className="font-body text-sm md:text-base text-black/70 mt-2.5 md:mt-4 leading-relaxed">{b.note}</p>
                  {b.addresses && (
                    <div className="mt-2.5 md:mt-3 flex flex-col gap-1.5">
                      {b.addresses.map((a) => (
                        <a
                          key={a.street}
                          href={mapUrl(b.name, a)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`bakery-card-${i}-map-${a.street.replace(/\s+/g, "-")}`}
                          className="inline-flex items-center gap-1.5 font-body font-700 text-sm text-black/70 hover:text-[#DF3226] transition-colors w-fit"
                        >
                          <MapPin size={14} className="text-[#DF3226] shrink-0" />
                          <span className="underline decoration-black/20 underline-offset-2 hover:decoration-[#DF3226]">
                            {a.street}
                          </span>
                          <span className="text-black/40">· {a.locality}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
            <Reveal y={40}>
              <Link
                to="/advertise"
                data-testid="bakery-card-missing"
                className="group bg-white border-2 border-dashed border-zinc-300 hover:border-[#DF3226] hover:-translate-y-1 transition-all p-4 md:p-7 h-full flex flex-col"
              >
                <span className="hidden md:inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                  <Croissant size={12} /> THIS SPOT IS OPEN
                </span>
                <h3 className="font-display font-900 tracking-tight text-xl md:text-3xl mt-1 md:mt-4 leading-none">
                  Is your bakery missing?
                </h3>
                <p className="hidden md:block font-body text-sm md:text-base text-black/70 mt-4 leading-relaxed">
                  Put it in front of travellers already planning their Copenhagen mornings.
                </p>
                <span className="mt-auto pt-2 md:pt-4 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] group-hover:gap-2.5 transition-all">
                  Advertise with us
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* the wienerbrød field guide */}
      <section data-testid="bakeries-wienerbrod" className="bg-[#FCEFC5] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-10 pb-16 md:py-20">
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">THE WIENERBRØD FIELD GUIDE</p>
          </div>
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-6xl">
            Know your pastries
          </h2>
          <p className="font-body text-base md:text-lg text-black/75 max-w-2xl mt-5 leading-relaxed">
            What the rest of the world calls "a Danish", Danes call <em>wienerbrød</em> — Viennese
            bread. It covers a whole family, and every one has a name worth learning:
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 md:gap-3 mt-8">
            {WIENERBROD.map((w) => (
              <div
                key={w.name}
                data-testid={`wienerbrod-${w.slug}`}
                className="bg-white border border-zinc-200 shadow-brut px-3.5 py-3 md:px-5 md:py-4 hover:-translate-y-0.5 transition-transform"
              >
                <p className="font-display font-900 tracking-tight text-base md:text-xl leading-tight">{w.name}</p>
                <p className="font-body text-xs md:text-sm text-black/60 mt-1.5">{w.note}</p>
              </div>
            ))}
          </div>
          <p className="font-editorial italic font-600 text-lg md:text-xl text-black/70 max-w-2xl mt-8">
            No single bakery stocks the full line-up — but string a few of the places above into a
            bakery tour and you might just catch them all.
          </p>
        </div>
      </section>


      {/* fastelavnsboller — the seasonal special */}
      <section data-testid="bakeries-fastelavn" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16 flex flex-col lg:flex-row gap-10 lg:items-center">
          <div className="flex-1">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">
              THE SEASONAL SPECIAL
            </p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-5xl">
              The Fastelavnsbolle
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 mt-8">
              <div>
                <p className="font-body font-800 text-xs tracking-widest text-[#DF3226] mb-2">WHEN</p>
                <p className="font-body text-sm md:text-base text-white/70 leading-relaxed">
                  From early January until Fastelavn — Denmark's Shrovetide, in February or early
                  March. After that they're gone for the year.
                </p>
              </div>
              <div>
                <p className="font-body font-800 text-xs tracking-widest text-[#DF3226] mb-2">WHY</p>
                <p className="font-body text-sm md:text-base text-white/80 leading-relaxed">
                  Once the sweet send-off before Lent — now it's become an art form. Every bakery
                  makes its own: with cream, custard, berries, pistachio, chocolate… Calorie bombs,
                  all of them — don't be on a diet in March.
                </p>
              </div>
              <div>
                <p className="font-body font-800 text-xs tracking-widest text-[#DF3226] mb-2">WHERE</p>
                <p className="font-body text-sm md:text-base text-white/70 leading-relaxed">
                  Every bakery on this page bakes its own in season — and they're all in
                  competition to make the best one.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:w-[360px] shrink-0">
            <img
              src="https://thumb.wikimedia.org/wikipedia/commons/thumb/9/90/Fastelavnsboller_%28Lagkagehuset%29.jpg/960px-Fastelavnsboller_%28Lagkagehuset%29.jpg"
              alt="A bakery counter full of fastelavnsboller — cream, custard, berry and chocolate varieties"
              loading="lazy"
              className="w-full h-64 lg:h-80 object-cover rounded-2xl border border-black/20 shadow-brut"
            />
            <p className="font-editorial italic text-white/60 text-sm mt-3">
              The bakery counter in February — pick your calorie bomb.
            </p>
          </div>
        </div>
      </section>


      <section className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <BakeryCrawlCard linkToGuide />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Hungry yet?<br />
            <span className="font-editorial italic font-600">Build it into your day</span>
          </h2>
          <Link
            to="/planner"
            data-testid="bakeries-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full border border-white/20 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            Open the planner
          </Link>
        </div>
      </section>

      {/* bookable tour — the Copenhagen tasting tour */}
      <GygTourBand tourId="621540" label="BOOK A TOUR" />
      <ExploreLinks
        testid="explore-bakeries"
        links={[
          { label: "What to eat next — the Danish food guide", to: "/danish-food" },
          { label: "Sourdough central — Nørrebro area guide", to: "/areas/norrebro" },
          { label: "Lunch after the pastry queue — smørrebrød, ranked", to: "/smorrebrod" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="bakeries-faq" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Bakery questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`bakeries-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                  {(f.links || (f.link ? [f.link] : [])).map((l, j) => (
                    <Link
                      key={l.to + l.label}
                      to={l.to}
                      data-testid={`bakeries-faq-link-${i}-${j}`}
                      className="group flex items-center gap-2 mt-3 font-body font-700 text-base text-black/60 hover:text-[#DF3226] transition-colors w-fit"
                    >
                      <ArrowRight size={16} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                      {l.label}
                    </Link>
                  ))}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar />
    </main>
  );
}
